import React from 'react';
import { useHistory } from 'react-router';

import { useProfessionalSignUp } from 'src/modules/app/hooks';
import { ResetPasswordForm } from 'src/shared/components';
import {
  CanonicalLink as MetaCanonicalLink,
  Title as MetaTitle,
} from 'src/shared/components/Meta';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useActivateMutation } from 'src/shared/graphql/generated';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { TwoColumn } from 'src/shared/layouts';
import { Route } from 'src/shared/route';
import { identify } from 'src/shared/telemetry';

const Activate = () => {
  const [activate, { error, loading }] = useActivateMutation();
  const t = useTranslate(TranslationNamespace.SIGN_UP);
  const history = useHistory();
  const { userDispatch } = useProfessionalSignUp();

  const onSubmit = async (formData: { password: string }, token: string) => {
    try {
      const { data } = await activate({
        variables: { ...formData, token },
      });

      if (data) {
        const {
          user: {
            id,
            email,
            firstName,
            lastName,
            mobilePhone,
            orgs,
            intercomSha,
          },
          jwt: { accessToken, refreshToken },
        } = data.resetPassword;

        try {
          await identify({
            email,
            userId: id,
            intercomSha,
          });
        } finally {
          void defaultLocalStorageAdapter.setAccessToken(accessToken);
          void defaultLocalStorageAdapter.setRefreshToken(refreshToken);

          userDispatch({
            type: 'UPDATE_EXTERNAL_PROFILE',
            payload: {
              company: orgs[0]?.name ?? '',
              email,
              givenName: firstName ?? '',
              familyName: lastName ?? '',
              mobilePhone: mobilePhone ?? '',
              needsUserUpdate: true,
            },
          });

          history.push(
            Route.SIGN_UP_PROFESSIONAL_ORGANIZATION.path +
              new URL(window.location.href).search,
          );
        }
      }
    } catch {
      // TODO: expected authentication errors should probably be
      // handled in an Apollo error link at the client level
    }
  };

  return (
    <>
      <MetaCanonicalLink pathname={Route.LOGIN.path} />
      <MetaTitle title={t('activate.activate')} />
      <TwoColumn.Header
        heading={t('activate.professionalAccount')}
        subheading={t('activate.weNeedInformation')}
      />
      <TwoColumn.Main>
        <ResetPasswordForm
          error={error}
          loading={loading}
          onSubmit={onSubmit}
          submitText={t('createAccount')}
        />
      </TwoColumn.Main>
    </>
  );
};

export { Activate };
